/**
 * Saker V1.2 (issue #286, handoff docs/design/saker-v1-handoff/HANDOFF.md at e5d63de1a; first built for #147):
 * what needs the manager, one task per decision, in three levels: the overview (the period and team, four status
 * tabs, a search, what is urgent, the four work areas, the other periods and the information that is not a task),
 * one area's queue, and one task (CaseDetail) with a pager over that queue. A finished task shows what was saved
 * and what remains, then the next. Beside the week it is the side column ('panel'); on a phone a closed summary
 * opens the same levels as a sheet ('bar').
 *
 * Membership (period, team, information-only) is saker.ts's, so every count, list and the pager agree. The page
 * writes through its own services (onAct) and hands back the outcome; a task moves on only when that outcome says
 * saved. Where the reader is, the page keeps in the address (onNav), so Back, a return from Kontroll and a reload
 * come back to the same task and scope. Styles: ReviewBar.module.css.
 */
import { type TaskState, type TaskWrite, useSakerStates, useSakerWrite } from '@/api/saker';
import { T } from '@/i18n';
import { today as todayOslo } from '@/rules';
import { askBeforeLeaving } from '@/shell/context';
import { Drawer, Skeleton, toast } from '@/ui';
import { Icon, type IconName } from '@/ui/icons';
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaseDetail, stateOf } from './CaseDetail';
import css from './ReviewBar.module.css';
import {
  ApproveDialog,
  DecisionDialog,
  DeferDialog,
  HelpDialog,
  PauseDialog,
  PlanDialog,
  ScopeDialog,
  type WriteProblem,
} from './SakerDialogs';
import type { ActInput, CaseAction, CaseOutcome, ReviewCase, SourceGap } from './reviewBar.types';
import {
  AREA_OF,
  type Area,
  type DoneTask,
  type Period,
  type SakerTab,
  type Scope,
  TABS,
  deferralsOf,
  doneIn,
  doneOf,
  drafts,
  dueDeferrals,
  latestOf,
  listsOf,
  matches,
  rememberDone,
  stateKey,
} from './saker';

/** Where the reader is in Saker, as the page keeps it in the address (S03, S09, S15). */
export interface SakerNav {
  tab: SakerTab;
  period: Period;
  area: Area | null;
  key: string | null;
  q: string;
}
export const NAV0: SakerNav = { tab: 'open', period: 'current', area: null, key: null, q: '' };

export interface ReviewBarProps {
  cases: ReviewCase[];
  /** waiting on an employee: the Venter tab, never counted as work */
  waiting: ReviewCase[];
  /** "1 nå, 3 forespørsler, …", kept for the page's own summary */
  breakdown: string;
  /** what the counts cover, said as it is */
  scope: string;
  /** reads that have not answered or failed, with the categories they feed: not known clear (RF1, S17) */
  gaps: SourceGap[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  /** run an action; the bar shows the outcome and moves on only when it says saved */
  onAct: (c: ReviewCase, a: CaseAction, input?: ActInput) => Promise<CaseOutcome>;
  onLink: (to: string) => void;
  /** a shift in the side panel, on its clock times */
  onShift?: (shiftId: number) => void;
  /** a week item's way in: its shift, day or week rule */
  onOpen: (item: string) => void;
  /** Kontroll on exactly a worklist's records, with Saker as the origin (S09) */
  onKontroll?: ((c: ReviewCase) => void) | undefined;
  /** what the page adds under a case's actions: Hugin's word on it (#131) */
  extra?: ((c: ReviewCase) => ReactNode) | undefined;
  /** what answering does: an absence's deduction, balance and shifts (review of #147, F2) */
  impact?: ((c: ReviewCase) => ReactNode) | undefined;
  /** more for the case's description: how an absence's deduction is counted */
  about?: ((c: ReviewCase) => ReactNode) | undefined;
  onPerson: (id: number) => void;
  onDay: (date: string) => void;
  /** the case on screen, for the plan to mark its shift; null when none is open */
  onActive: (c: ReviewCase | null) => void;
  /** whether a case's date is outside the period the plan shows */
  outside: (c: ReviewCase) => boolean;
  onShowPeriod: (c: ReviewCase) => void;
  /** open on the first case from the start (fixtures, a deep link) */
  defaultOpen?: boolean;
  /** a card's dot or a warning's count (#146): these cases, named by `label` */
  focus?: { label: string; keys: string[]; n: number } | null | undefined;
  /** the keys Saker is filtered to, or null: the page outlines their shifts (owner 2026-10-25) */
  onScope?: ((keys: string[] | null) => void) | undefined;
  /** 'panel' the side column beside the week; 'bar' the phone's summary, the levels in a sheet */
  layout?: 'bar' | 'panel';
  /** ask Hugin about an area's cases; the words go to the docked chat */
  onAskHugin?: ((text: string) => void) | undefined;
  /** the company, so what was put off and what was finished are kept per company */
  laterKey?: string | undefined;
  /** the days the plan shows; absent, every dated case is in the period (fixtures) */
  days?: string[] | undefined;
  /** «Uke 45, 2.–8. nov.» */
  periodLabel?: string | undefined;
  /** «uke 45», for «Tilbake til uke 45» */
  periodShort?: string | undefined;
  /** the team Saker and the plan show, or null for Alle team */
  team?: Scope['team'] | undefined;
  /** «Endre»: the weeks and teams to choose from, and the page's own setter (S03) */
  weeks?: { monday: string; label: string }[] | undefined;
  monday?: string | undefined;
  teams?: { id: number; name: string }[] | undefined;
  teamId?: number | null | undefined;
  onScopeChange?: ((v: { monday: string; teamId: number | null }) => void) | undefined;
  /** the reader may look but change nothing here: said once, and no write is offered (S16) */
  readOnly?: boolean | undefined;
  /** the company has no people or shifts yet: the setup state, not an all-clear (S16) */
  firstUse?: boolean | undefined;
  today?: string | undefined;
  /** where the reader is, from the address, and where they go (S03, S09, S15) */
  nav?: SakerNav | undefined;
  onNav?: ((n: SakerNav) => void) | undefined;
}

/** the list level: one work area, or the cases a card's dot or a warning asked for */
type List = { kind: 'area'; area: Area } | { kind: 'scope'; label: string; keys: string[] };

/** The four work areas (S01); neutral icons, the count the only figure. */
export const AREAS: { a: Area; name: string; sub: string; icon: IconName }[] = [
  { a: 'na', name: 'Stempling', sub: 'Tider som mangler', icon: 'clock' },
  { a: 'timer', name: 'Timer', sub: 'Registreringer før lønn', icon: 'check' },
  { a: 'planlegg', name: 'Vaktplan', sub: 'Vakter og forespørsler', icon: 'calendar' },
  { a: 'lonn', name: 'Lønn', sub: 'Opplysninger som mangler', icon: 'banknote' },
];
const areaName = (a: Area) => T(AREAS.find((x) => x.a === a)?.name ?? '');

const DAY = 864e5;
/** Haster nå (the prototype's rule): the case's shift is running or starts within 24 hours. */
export function urgent(c: ReviewCase, now: number): boolean {
  if (!c.span) return false;
  return Date.parse(c.span.start) - DAY <= now && Date.parse(c.span.end) > now;
}

const tasks = (n: number) => (n === 1 ? T('1 oppgave') : T('{n} oppgaver', { n }));

function useOnline() {
  const [on, setOn] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => {
    const f = () => setOn(navigator.onLine !== false);
    window.addEventListener('online', f);
    window.addEventListener('offline', f);
    return () => {
      window.removeEventListener('online', f);
      window.removeEventListener('offline', f);
    };
  }, []);
  return on;
}

type Editor =
  | { kind: 'pause' | 'plan' | 'defer' | 'approve'; c: ReviewCase; a?: CaseAction }
  | { kind: 'decide'; c: ReviewCase; a: Extract<CaseAction, { kind: 'decide' }> };

export function ReviewBar(p: ReviewBarProps) {
  const panel = p.layout === 'panel';
  const today = p.today ?? todayOslo();
  const company = p.laterKey;
  const online = useOnline();
  // the phone's sheet; the side column is always open
  const [open, setOpen] = useState(!!p.defaultOpen || !!p.nav?.key);
  const start = p.nav ?? NAV0;
  const [tab, setTab] = useState<SakerTab>(start.tab);
  const [period, setPeriod] = useState<Period>(start.period);
  const [list, setList] = useState<List | null>(() =>
    p.defaultOpen && p.cases[0]
      ? { kind: 'area', area: AREA_OF[p.cases[0].cat] }
      : start.area
        ? { kind: 'area', area: start.area }
        : null,
  );
  const [activeKey, setActiveKey] = useState<string | null>(
    p.defaultOpen ? (p.cases[0]?.key ?? null) : start.key,
  );
  const [search, setSearch] = useState(start.q);
  const [allUrgent, setAllUrgent] = useState(false);
  // Utsatt and Ferdig are the company's, on the server, each change with who, when and why (#286 S14, S15)
  const statesQ = useSakerStates();
  const writeState = useSakerWrite();
  const latest = useMemo(() => latestOf(statesQ.data), [statesQ.data]);
  const deferrals = useMemo(() => deferralsOf(statesQ.data), [statesQ.data]);
  const [doneTick, setDoneTick] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: doneTick redraws when only the words in memory changed
  const done = useMemo<DoneTask[]>(() => doneOf(statesQ.data, company), [statesQ.data, company, doneTick]);
  /** one decision about a task, sent against the row this page saw; a refusal is said, never swallowed (S18) */
  const record = async (
    w: Omit<TaskWrite, 'expected'> & { team?: string | null },
  ): Promise<TaskState | null> => {
    const seen = latest.get(stateKey(w.key, w.team ?? null));
    const expected = seen && (seen.action === 'defer' || seen.action === 'done') ? seen.id : null;
    const r = await writeState.mutateAsync({ ...w, expected });
    if (r.ok) return r.row;
    toast(
      r.conflict
        ? T('Noen andre har endret oppgaven mens du holdt på. Listen er lest på nytt; se hva som gjelder nå.')
        : r.message || T('Vi vet ikke om endringen ble lagret. Sakene leses på nytt før du gjør noe mer.'),
    );
    return null;
  };
  const [mode, setMode] = useState<'actions' | 'form'>('actions');
  const [busy, setBusy] = useState(false);
  // Finn erstatter is open in the page's panel: the case waits for its answer (candidate addendum, AI-02)
  const [parked, setParked] = useState<string | null>(null);
  // a save in flight holds the review where it is: no stepping, no level change, no closing (F5)
  const busyRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Extract<CaseOutcome, { kind: 'partial' }> | null>(null);
  const [gone, setGone] = useState<string | null>(null);
  /** a finished task: what was saved and what remains, before the next one (S15) */
  const [finished, setFinished] = useState<{
    c: ReviewCase;
    text: string;
    detail?: string;
    undo?: () => Promise<CaseOutcome>;
  } | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [problem, setProblem] = useState<WriteProblem | null>(null);
  const [help, setHelp] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [announce, setAnnounce] = useState('');
  const titleRef = useRef<HTMLHeadingElement>(null);
  const headRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const tabRefs = useRef<Partial<Record<SakerTab, HTMLButtonElement | null>>>({});
  const [focusTab, setFocusTab] = useState<SakerTab | null>(null);
  useEffect(() => {
    if (!focusTab) return;
    tabRefs.current[focusTab]?.focus();
    setFocusTab(null);
  }, [focusTab]);
  /** the control a dialog was opened from: focus goes back there when it closes without a save (S08) */
  const opener = useRef<HTMLElement | null>(null);
  const openEditor = (e: Editor) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setEditor(e);
  };
  const closeEditor = () => {
    setEditor(null);
    requestAnimationFrame(() => {
      const o = opener.current;
      (o?.isConnected ? o : titleRef.current)?.focus({ preventScroll: true });
    });
  };

  // ---- membership: one place decides what each count, list and the pager are about (saker.ts) ----
  const scope: Scope = useMemo(
    () => ({
      days: p.days ?? [...new Set(p.cases.map((c) => c.date).filter((d): d is string => !!d))],
      team: p.team ?? null,
    }),
    [p.days, p.cases, p.team],
  );
  const put = useMemo(() => dueDeferrals(deferrals, today), [deferrals, today]);
  const lists = useMemo(
    () => listsOf({ cases: p.cases, waiting: p.waiting }, scope, period, put),
    [p.cases, p.waiting, scope, period, put],
  );
  const otherOpen = useMemo(
    () => listsOf({ cases: p.cases, waiting: p.waiting }, scope, 'other', put).open.length,
    [p.cases, p.waiting, scope, put],
  );
  const doneHere = doneIn(done, scope, period);
  const pool =
    tab === 'waiting'
      ? lists.waiting
      : tab === 'deferred'
        ? lists.deferred
        : tab === 'open'
          ? lists.open
          : [];
  const byKey = useMemo(() => {
    const m = new Map<string, ReviewCase>();
    for (const c of [...lists.open, ...lists.deferred, ...lists.waiting, ...lists.info]) m.set(c.key, c);
    // a task from a card's dot may sit in another period: the whole set answers it
    for (const c of [...p.cases, ...p.waiting]) if (!m.has(c.key)) m.set(c.key, c);
    return m;
  }, [lists, p.cases, p.waiting]);
  const tabOf = (c: ReviewCase): SakerTab =>
    p.waiting.some((w) => w.key === c.key) ? 'waiting' : put.has(c.key) ? 'deferred' : 'open';
  // the list the pager steps through
  const shown =
    list?.kind === 'scope'
      ? [...p.cases, ...p.waiting].filter((c) => list.keys.includes(c.key))
      : list
        ? pool.filter((c) => AREA_OF[c.cat] === list.area)
        : search
          ? pool.filter((c) => matches(c, search, areaName))
          : pool;
  const listName = list ? (list.kind === 'scope' ? list.label : areaName(list.area)) : T('Søk');
  // the case on screen, read after an await: an outcome belongs to the case that started it (F5)
  const activeRef = useRef<string | null>(null);
  const active = activeKey ? byKey.get(activeKey) : undefined;
  const at = active ? shown.findIndex((c) => c.key === active.key) : -1;
  activeRef.current = activeKey;
  const visible = panel || open;
  const canWrite = !p.readOnly && online;

  // the address follows where the reader is (S03, S09, S15); the page decides how
  // biome-ignore lint/correctness/useExhaustiveDependencies: the place is the signal, the callback may change
  useEffect(() => {
    p.onNav?.({
      tab,
      period,
      area: list?.kind === 'area' ? list.area : null,
      key: visible ? activeKey : null,
      q: search,
    });
  }, [tab, period, list?.kind === 'area' ? list.area : null, activeKey, search, visible]);

  // the plan marks the case on screen
  // biome-ignore lint/correctness/useExhaustiveDependencies: tell the page when the case changes, not on every render
  useEffect(() => {
    p.onActive(visible && active ? active : null);
  }, [visible, active?.key]);
  // the cases a card's dot or a warning listed: the plan outlines their shifts
  const scopeKeys = list?.kind === 'scope' && visible ? list.keys.join() : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the keys are the signal, the callback may change
  useEffect(() => {
    p.onScope?.(list?.kind === 'scope' && visible ? list.keys : null);
    return () => p.onScope?.(null);
  }, [scopeKeys]);

  // a case that went away while it was on screen: resolved or changed somewhere else (UC12, UC13)
  useEffect(() => {
    if (activeKey && !byKey.has(activeKey) && gone !== activeKey && !finished && !p.loading)
      setGone(activeKey);
  }, [activeKey, byKey, gone, finished, p.loading]);

  const say = (m: string) => {
    setAnnounce('');
    requestAnimationFrame(() => setAnnounce(m));
  };
  const reset = () => {
    setMode('actions');
    setError(null);
  };
  // a correction typed and not saved: stepping or leaving the case asks first (review of #147, item 4)
  const onDirty = useCallback((d: boolean) => setMode(d ? 'form' : 'actions'), []);
  const go = (key: string | null, heading = true) => {
    if (busyRef.current) return;
    reset();
    setGone(null);
    setFinished(null);
    setActiveKey(key);
    if (heading)
      requestAnimationFrame(() => (key ? titleRef : headRef).current?.focus({ preventScroll: true }));
  };
  /** Moving away from a half-typed correction asks first, with the app's own leave question (S08). */
  const away = (label: string, f: () => void) =>
    mode === 'form'
      ? askBeforeLeaving(label, f, {
          // discarded is gone: the kept draft too (S08)
          discard: () => {
            if (activeRef.current) drafts.write(activeRef.current, null);
            reset();
          },
        })
      : f();
  const openCase = (c: ReviewCase, from?: List) => {
    if (busyRef.current) return;
    away(T('Annen sak'), () => {
      setTab(tabOf(c));
      setList(from ?? { kind: 'area', area: AREA_OF[c.cat] });
      setReceipt(null);
      go(c.key);
    });
  };
  const openList = (l: List | null) => {
    if (busyRef.current) return;
    away(T('Annen sak'), () => {
      setList(l);
      go(null);
    });
  };
  const back = () => (active || gone || finished ? openList(list) : openList(null));
  const step = (d: 1 | -1) => {
    if (busyRef.current || at < 0) return;
    const next = shown[at + d];
    if (next) away(T('Annen sak'), () => go(next.key));
  };
  const pickTab = (b: SakerTab, focus = false) => {
    if (busyRef.current) return;
    away(T('Annen sak'), () => {
      setTab(b);
      setList(null);
      setAllUrgent(false);
      go(null, !focus);
      // the tab follows the keyboard: focus moves with the selection (S04, S20)
      if (focus) setFocusTab(b);
    });
  };
  const pickPeriod = (x: Period) => {
    if (busyRef.current) return;
    away(T('Annen periode'), () => {
      setPeriod(x);
      setTab('open');
      setList(null);
      setSearch('');
      go(null);
    });
  };
  /** Arrow keys, Home and End move between the status tabs, and the tab follows focus (S04, S20). */
  const tabKeys = (e: KeyboardEvent<HTMLButtonElement>, b: SakerTab) => {
    const keys = TABS.map(([k]) => k);
    const i = keys.indexOf(b);
    const n =
      e.key === 'ArrowRight'
        ? (i + 1) % keys.length
        : e.key === 'ArrowLeft'
          ? (i - 1 + keys.length) % keys.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? keys.length - 1
              : -1;
    if (n < 0) return;
    e.preventDefault();
    pickTab(keys[n] as SakerTab, true);
  };

  // a card's dot or a warning's count: one case opens, more are listed (prototype; owner 2026-10-25)
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new press opens
  useEffect(() => {
    const f = p.focus;
    if (!f || busyRef.current) return;
    setOpen(true);
    setSearch('');
    setReceipt(null);
    const one = f.keys.length === 1 ? byKey.get(f.keys[0] as string) : undefined;
    if (one) openCase(one);
    else {
      setTab('open');
      setList({ kind: 'scope', label: f.label, keys: f.keys });
      go(null);
    }
    requestAnimationFrame(() => rootRef.current?.scrollIntoView?.({ block: 'nearest' }));
  }, [p.focus?.n]);

  // J next, K previous in a case; never while typing, never with a modifier (UC3). Escape goes up a level.
  useEffect(() => {
    if (!visible) return;
    const f = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (busyRef.current || editor || help || scopeOpen) return;
      if (
        panel &&
        e.key === 'Escape' &&
        rootRef.current?.contains(t) &&
        (list || active || finished || gone)
      ) {
        e.preventDefault();
        back();
        return;
      }
      if (!active || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (t?.closest('input, select, textarea, [contenteditable="true"]')) return;
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        step(1);
      } else if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        step(-1);
      }
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  });

  /** A saved outcome: the task is finished here, recorded under Ferdig, and said (S15). */
  const finish = (c: ReviewCase, r: Extract<CaseOutcome, { kind: 'saved' }>) => {
    // what remains in other teams is part of the result, never left out (S10)
    const elsewhere =
      c.elsewhere && p.team
        ? c.elsewhere === 1
          ? T('1 registrering gjenstår i andre team.')
          : T('{n} registreringer gjenstår i andre team.', { n: c.elsewhere })
        : '';
    const text = [r.text, elsewhere].filter(Boolean).join(' ');
    const date = c.date ?? c.members?.[0]?.date;
    const team = p.team?.name ?? null;
    rememberDone(company, {
      key: c.key,
      area: AREA_OF[c.cat],
      title: c.what ?? c.title,
      person: c.person?.name,
      date,
      team,
      result: [text, r.detail].filter(Boolean).join(' '),
      at: new Date().toISOString(),
    });
    setDoneTick((n) => n + 1);
    // the fact is saved by now; that the task is finished is recorded beside it, for Ferdig and its history
    void record({ key: c.key, action: 'done', team, area: AREA_OF[c.cat], ...(date ? { date } : {}) });
    drafts.write(c.key, null);
    setFinished({ c, text, ...(r.detail ? { detail: r.detail } : {}), ...(r.undo ? { undo: r.undo } : {}) });
    reset();
    say(T('Oppgaven er ferdig. {t}', { t: text }));
    requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }));
  };

  const act = async (c: ReviewCase, a: CaseAction, input?: ActInput): Promise<CaseOutcome | null> => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setProblem(null);
    if (a.kind === 'cover') setParked(c.title);
    let r: CaseOutcome;
    try {
      r = await p.onAct(c, a, input);
    } finally {
      busyRef.current = false;
      setBusy(false);
      setParked(null);
    }
    // an outcome is only ever shown on its own case
    if (activeRef.current !== c.key) return r;
    if (r.kind === 'saved') {
      setEditor(null);
      finish(c, r);
    } else if (r.kind === 'partial') {
      setEditor(null);
      setReceipt(r);
      reset();
    } else if (r.kind === 'refused') {
      if (editor) setProblem({ message: r.message, changed: r.changed });
      else setError(r.message);
    } else if (r.kind === 'cancelled') return r;
    else {
      const m = T('Vi vet ikke om endringen ble lagret. Sakene leses på nytt før du gjør noe mer.');
      if (editor) setProblem({ message: m });
      else setError(m);
    }
    return r;
  };

  const undoFinished = async () => {
    const f = finished;
    if (!f?.undo || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    let u: CaseOutcome;
    try {
      u = await f.undo();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (u.kind === 'saved') {
      toast(u.detail ? `${u.text}. ${u.detail}` : u.text);
      void record({ key: f.c.key, action: 'reopen', team: p.team?.name ?? null });
      setFinished(null);
      go(f.c.key);
    } else
      setReceipt({
        kind: 'partial',
        text: f.text,
        message:
          u.kind === 'refused'
            ? T('Angring feilet: {m}', { m: u.message })
            : T('Vi vet ikke om angringen ble lagret.'),
      });
  };

  /** Ta senere: a date to follow up (S14); from Utsatt it is taken up now */
  const onLater = (c: ReviewCase) => {
    if (busyRef.current) return;
    if (put.has(c.key)) {
      void record({ key: c.key, action: 'resume' }).then((row) => {
        if (!row) return;
        setTab('open');
        say(T('Oppgaven er tilbake under Å følge opp.'));
      });
      return;
    }
    openEditor({ kind: 'defer', c });
  };
  const defer = async (c: ReviewCase, until: string, reason?: string) => {
    const rest = shown.filter((x) => x.key !== c.key);
    const next = rest[Math.min(Math.max(at, 0), rest.length - 1)] ?? null;
    const date = c.date ?? c.members?.[0]?.date;
    const row = await record({
      key: c.key,
      action: 'defer',
      due: until,
      area: AREA_OF[c.cat],
      ...(date ? { date } : {}),
      ...(reason ? { reason } : {}),
    });
    setEditor(null);
    if (!row) return;
    go(next?.key ?? null);
    // Angre takes it up again, sent against the deferral just made and recorded like any other change
    toast(T('Oppgaven er utsatt til {d}', { d: until.split('-').reverse().slice(0, 2).join('.') }), () => {
      void writeState.mutateAsync({ key: c.key, action: 'resume', expected: row.id });
    });
  };

  const onAction = (c: ReviewCase, a: CaseAction) => {
    if (a.kind === 'link') p.onLink(a.to);
    else if (a.kind === 'open') p.onOpen(a.item);
    else if (a.kind === 'shift') p.onShift?.(a.shiftId);
    else if (a.kind === 'person') p.onPerson(a.id);
    else if (a.kind === 'kontroll') p.onKontroll?.(c);
    else if (a.kind === 'pause' || a.kind === 'plan') {
      setProblem(null);
      openEditor({ kind: a.kind, c, a });
    } else if (a.kind === 'decide' && a.what === 'absence') {
      setProblem(null);
      openEditor({ kind: 'decide', c, a });
    } else if (a.kind === 'approve' && c.members?.length) {
      setProblem(null);
      openEditor({ kind: 'approve', c, a });
    } else if (a.kind !== 'contact') void act(c, a);
  };

  // ---- what is known (S16, S17) ----
  const failedGaps = p.gaps.filter((g) => g.state === 'failed');
  /** what is not known about an area: a read failed, or has not answered; null when all did (RF1) */
  const gapOf = (a: Area | null): 'failed' | 'loading' | null => {
    const g = p.gaps.filter((x) => a == null || x.cats.some((c) => AREA_OF[c] === a));
    return g.some((x) => x.state === 'failed') ? 'failed' : g.length ? 'loading' : null;
  };
  const known = gapOf(null);
  const unknown = p.failed && !p.cases.length;
  const counting = !p.cases.length && (p.loading || known === 'loading');
  const figure = (n: number) => (known === 'failed' ? `${n}+` : counting ? '…' : String(n));
  const now = Date.now();
  const tabCount: Record<SakerTab, number> = {
    open: lists.open.length,
    waiting: lists.waiting.length,
    deferred: lists.deferred.length,
    done: doneHere.length,
  };

  const row = (c: ReviewCase, withArea = false, from?: List) => {
    const s = stateOf(c, { waiting: tab === 'waiting', later: put.has(c.key) });
    const tag = tab !== 'open' || s.word !== T('Venter på deg');
    const meta = [
      c.dateLabel ?? c.when,
      withArea ? areaName(AREA_OF[c.cat]) : '',
      c.findings?.length ? T('{n} forhold, én oppgave', { n: c.findings.length }) : '',
      c.members?.length ? T('{n} gjenstår', { n: c.members.length }) : '',
      put.has(c.key)
        ? T('Følg opp {d}', { d: (deferrals[c.key] ?? '').split('-').reverse().slice(0, 2).join('.') })
        : '',
    ].filter(Boolean);
    return (
      <li key={c.key}>
        <button
          type="button"
          className={css.caseRow}
          data-testid="review-row"
          data-key={c.key}
          aria-current={c.key === activeKey ? 'true' : undefined}
          onClick={() => openCase(c, from)}
        >
          <span className={css.rowCopy}>
            <span className={css.rowTitle}>{c.person?.name ?? c.title}</span>
            <span className={css.rowSub}>{c.person ? c.title : (c.what ?? '')}</span>
            <span className={css.rowMeta}>{meta.join(', ')}</span>
            {tag && (
              <span className={css.tag} data-state={s.kind}>
                {s.word}
              </span>
            )}
          </span>
          <Icon name="chevronRight" size={18} strokeWidth={1.7} />
        </button>
      </li>
    );
  };
  const empty = (
    title: string,
    text: string,
    action?: { label: string; on: () => void; testid?: string },
  ) => (
    <div className={css.vempty} data-testid="review-empty">
      <Icon name="list" size={20} strokeWidth={1.7} />
      <h3>{title}</h3>
      <p>{text}</p>
      {action && (
        <button type="button" className={css.quiet} onClick={action.on} data-testid={action.testid}>
          {action.label}
        </button>
      )}
    </div>
  );

  const scopeBar = (
    <div className={css.scopeBar} data-testid="saker-scope-bar">
      <Icon name="calendar" size={18} strokeWidth={1.7} />
      <span className={css.scopeText}>
        <strong>{period === 'other' ? T('Andre perioder') : (p.periodLabel ?? T('Perioden du ser'))}</strong>
        {period === 'other' && p.periodLabel ? `, ${T('utenfor {p}', { p: p.periodLabel })}` : ''}
        <br />
        {p.team?.name ?? T('Alle team')}
      </span>
      {p.onScopeChange && p.weeks?.length ? (
        <button
          type="button"
          className={css.scopeChange}
          onClick={() => setScopeOpen(true)}
          aria-label={T('Endre periode og team')}
          data-testid="saker-scope-change"
        >
          {T('Endre')}
        </button>
      ) : null}
      <button
        type="button"
        className={css.iconBtn}
        aria-label={T('Slik fungerer Saker')}
        onClick={() => setHelp(true)}
        data-testid="saker-help-open"
      >
        <Icon name="info" size={18} strokeWidth={1.7} />
      </button>
    </div>
  );

  const notices = (
    <>
      {p.readOnly && (
        <div className={css.notice} data-testid="saker-readonly">
          <Icon name="info" size={18} strokeWidth={1.7} />
          <div>
            <strong>{T('Du har lesetilgang.')}</strong>
            <p>{T('Du ser oppgavene, men en leder med redigeringstilgang må gjøre endringene.')}</p>
          </div>
        </div>
      )}
      {!online && (
        <div className={css.notice} data-testid="saker-offline">
          <Icon name="warn" size={18} strokeWidth={1.7} />
          <div>
            <strong>{T('Du er frakoblet.')}</strong>
            <p>{T('Viser sist hentede oppgaver. De kan være utdatert. Koble til før du endrer noe.')}</p>
            <button type="button" className={css.quiet} onClick={p.onRetry}>
              {T('Prøv å koble til')}
            </button>
          </div>
        </div>
      )}
      {statesQ.isError && (
        <div className={css.notice} data-tone="warn" data-testid="saker-states-failed">
          <Icon name="warn" size={18} strokeWidth={1.7} />
          <div>
            <strong>{T('Kunne ikke hente hva som er utsatt og ferdig.')}</strong>
            <p>{T('Utsatte oppgaver kan stå under Å følge opp, og Ferdig kan være tom til de er hentet.')}</p>
            <button type="button" className={css.quiet} onClick={() => void statesQ.refetch()}>
              {T('Prøv igjen')}
            </button>
          </div>
        </div>
      )}
      {failedGaps.length > 0 && !unknown && (
        <div className={css.notice} data-tone="warn" data-testid="review-incomplete">
          <Icon name="warn" size={18} strokeWidth={1.7} />
          <div>
            <strong>
              {T('Kunne ikke hente {what}.', { what: failedGaps.map((g) => g.label).join(', ') })}
            </strong>
            <p>{T('De andre områdene er hentet. Totalen er foreløpig, og kan være for lav.')}</p>
            <button type="button" className={css.quiet} onClick={p.onRetry}>
              {T('Prøv igjen')}
            </button>
          </div>
        </div>
      )}
    </>
  );

  // the words of a finished task are in this page's memory only; after a reload the row says the area, the day and who
  const doneRow = (t: DoneTask) => (
    <li key={`${t.key}-${t.at}`} className={css.doneRow} data-testid="review-done-row">
      <Icon name="check" size={18} strokeWidth={1.7} />
      <span className={css.rowCopy}>
        <span className={css.rowTitle}>{t.person ?? t.title ?? areaName(t.area)}</span>
        {t.person && t.title && <span className={css.rowSub}>{t.title}</span>}
        <span className={css.rowMeta}>
          {t.result ??
            (t.by
              ? T('Ferdig {d} av {who}', {
                  d: new Date(t.at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }),
                  who: t.by,
                })
              : T('Avklart her {d}', {
                  d: new Date(t.at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }),
                }))}
        </span>
      </span>
    </li>
  );

  const overview = () => {
    if (unknown)
      return (
        <div className={css.state} data-testid="review-failed" role="alert">
          <Icon name="warn" size={22} strokeWidth={1.7} />
          <h2 ref={headRef} tabIndex={-1}>
            {T('Oppgavene kunne ikke hentes')}
          </h2>
          <p>{T('Vi vet ikke hva som trenger oppfølging i perioden. Perioden og teamet ditt er beholdt.')}</p>
          <button type="button" className={css.primary} onClick={p.onRetry}>
            {T('Prøv igjen')}
          </button>
        </div>
      );
    if (counting)
      return (
        <div className={css.state} data-testid="review-loading">
          <h2 ref={headRef} tabIndex={-1}>
            {T('Henter oppgaver')}
          </h2>
          <p>{T('Henter grunnlaget for perioden. Antallet er ikke kjent ennå.')}</p>
          <Skeleton rows={3} />
        </div>
      );
    if (p.firstUse)
      return (
        <div className={css.state} data-testid="review-first-use">
          <Icon name="list" size={22} strokeWidth={1.7} />
          <h2 ref={headRef} tabIndex={-1}>
            {T('Her samler vi det du må følge opp')}
          </h2>
          <p>
            {T(
              'Oppgaver vises når ansatte, vakter og registreringer er på plass. Du kan begynne med oppsettet.',
            )}
          </p>
          <button type="button" className={css.primary} onClick={() => p.onLink('/innstillinger')}>
            {T('Se oppsett')}
          </button>
        </div>
      );
    const found = search ? pool.filter((c) => matches(c, search, areaName)) : [];
    const urg = tab === 'open' && period === 'current' ? lists.open.filter((c) => urgent(c, now)) : [];
    const areas = AREAS.filter(
      (a) => pool.some((c) => AREA_OF[c.cat] === a.a) || (tab === 'open' && gapOf(a.a) === 'failed'),
    );
    const n = tabCount[tab];
    const sub =
      known === 'failed' && tab === 'open'
        ? T('{n} kjente oppgaver. Noe kunne ikke hentes.', { n })
        : tab === 'open'
          ? T('{t} trenger en avgjørelse.', { t: tasks(n) })
          : tab === 'waiting'
            ? T('{t} venter på andre.', { t: tasks(n) })
            : tab === 'deferred'
              ? T('{t} er utsatt.', { t: tasks(n) })
              : T('{t} er avklart.', { t: tasks(n) });
    return (
      <>
        <div className={css.intro}>
          <h2 ref={headRef} tabIndex={-1} data-testid="review-total">
            {period === 'other' ? T('Oppgaver fra andre perioder') : T('Dette følger du opp')}
          </h2>
          <p data-testid="review-sub">{sub}</p>
          <button type="button" className={css.textLink} onClick={() => setHelp(true)}>
            {T('Hva teller som én oppgave?')}
          </button>
        </div>
        <div className={css.tabs} role="tablist" aria-label={T('Oppgavestatus')}>
          {TABS.map(([b, label]) => (
            <button
              key={b}
              ref={(el) => {
                tabRefs.current[b] = el;
              }}
              type="button"
              role="tab"
              id={`saker-tab-${b}`}
              aria-selected={tab === b}
              aria-controls="saker-results"
              tabIndex={tab === b ? 0 : -1}
              data-testid={`review-bucket-${b === 'open' ? 'active' : b === 'deferred' ? 'later' : b}`}
              onClick={() => pickTab(b)}
              onKeyDown={(e) => tabKeys(e, b)}
            >
              <span>{T(label)}</span>
              <b>{b === 'done' ? String(tabCount.done) : figure(tabCount[b])}</b>
            </button>
          ))}
        </div>
        <div className={css.search}>
          <Icon name="search" size={18} strokeWidth={1.7} />
          <input
            type="search"
            autoComplete="off"
            placeholder={T('Søk etter person eller oppgave')}
            aria-label={T('Søk etter person eller oppgave')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="review-search"
          />
          {search && (
            <button
              type="button"
              className={css.iconBtn}
              aria-label={T('Tøm søket')}
              onClick={() => setSearch('')}
              data-testid="review-search-clear"
            >
              <Icon name="close" size={16} strokeWidth={1.7} />
            </button>
          )}
        </div>
        {notices}
        <div id="saker-results" role="tabpanel" aria-labelledby={`saker-tab-${tab}`} className={css.results}>
          {tab === 'done' ? (
            doneHere.length ? (
              <ul className={css.rows}>
                {doneHere
                  .filter(
                    (t) =>
                      !search ||
                      `${t.title ?? areaName(t.area)} ${t.person ?? ''}`
                        .toLocaleLowerCase('nb')
                        .includes(search.toLocaleLowerCase('nb')),
                  )
                  .map(doneRow)}
              </ul>
            ) : (
              empty(
                T('Ingen ferdige oppgaver ennå'),
                T('Når du avklarer en oppgave her, finner du resultatet i denne fanen på denne enheten.'),
              )
            )
          ) : search ? (
            found.length ? (
              <>
                <p className={css.count}>
                  {found.length === 1 ? T('1 treff') : T('{n} treff', { n: found.length })}
                </p>
                <ul className={css.rows}>{found.map((c) => row(c, true))}</ul>
              </>
            ) : (
              empty(
                T('Ingen treff'),
                T('Ingen oppgaver matcher «{q}» i dette utvalget. Prøv et annet navn eller tøm søket.', {
                  q: search,
                }),
                { label: T('Tøm søket'), on: () => setSearch(''), testid: 'review-search-reset' },
              )
            )
          ) : (
            <>
              {urg.length > 0 && (
                <section className={css.urgent} aria-label={T('Haster nå')} data-testid="review-urgent">
                  <p className={css.urgentHead}>
                    <Icon name="clock" size={16} strokeWidth={1.7} />
                    {T('Haster nå: pågår eller starter innen 24 timer')}
                  </p>
                  <ul className={css.rows}>{(allUrgent ? urg : urg.slice(0, 4)).map((c) => row(c, true))}</ul>
                  {urg.length > 4 && !allUrgent && (
                    <button
                      type="button"
                      className={css.textLink}
                      data-testid="review-urgent-all"
                      onClick={() => setAllUrgent(true)}
                    >
                      {T('Vis alle {n}', { n: urg.length })}
                    </button>
                  )}
                </section>
              )}
              {areas.length > 0 && <p className={css.section}>{T('Arbeidsområder')}</p>}
              {areas.map((a) => {
                const group = pool.filter((c) => AREA_OF[c.cat] === a.a);
                const g = gapOf(a.a);
                return (
                  <button
                    key={a.a}
                    type="button"
                    className={css.areaRow}
                    data-testid={`review-tab-${a.a}`}
                    onClick={() => openList({ kind: 'area', area: a.a })}
                  >
                    <Icon name={a.icon} size={18} strokeWidth={1.7} />
                    <span className={css.areaCopy}>
                      <b>{T(a.name)}</b>
                      <span>{T(a.sub)}</span>
                    </span>
                    {/* an area whose read failed is not known clear (RF1, S17) */}
                    {g === 'failed' && !group.length ? (
                      <span className={css.unknown}>{T('Ukjent')}</span>
                    ) : (
                      <span className={css.count2}>{g === 'failed' ? `${group.length}+` : group.length}</span>
                    )}
                    <Icon name="chevronRight" size={18} strokeWidth={1.7} />
                  </button>
                );
              })}
              {!areas.length &&
                (tab === 'deferred'
                  ? empty(
                      T('Ingen utsatte oppgaver'),
                      T('Oppgaver du velger å ta senere, vises her med dagen du valgte.'),
                    )
                  : tab === 'waiting'
                    ? empty(
                        T('Ingen venter på svar'),
                        T(
                          'Ingen oppgaver venter på andre i dette utvalget. Oppgaver du kan ta stilling til ligger under Å følge opp.',
                        ),
                      )
                    : period === 'current'
                      ? empty(
                          T('Ingen åpne oppgaver i dette utvalget'),
                          otherOpen
                            ? T('Det gjelder {p} for {team}. Eldre oppgaver er fortsatt åpne.', {
                                p: p.periodShort ?? T('perioden'),
                                team: (p.team?.name ?? T('alle team')).toLowerCase(),
                              })
                            : T('Det gjelder {p} for {team}, ikke hele firmaet.', {
                                p: p.periodShort ?? T('perioden'),
                                team: (p.team?.name ?? T('alle team')).toLowerCase(),
                              }),
                          otherOpen
                            ? { label: T('Se andre perioder'), on: () => pickPeriod('other') }
                            : undefined,
                        )
                      : empty(
                          T('Ingen åpne oppgaver utenfor perioden'),
                          T('For {team}.', { team: (p.team?.name ?? T('alle team')).toLowerCase() }),
                        ))}
            </>
          )}
        </div>
        <div className={css.more}>
          {period === 'current' ? (
            <button
              type="button"
              className={css.otherBtn}
              onClick={() => pickPeriod('other')}
              data-testid="review-elsewhere"
            >
              <span>{T('{t} i andre perioder', { t: tasks(otherOpen) })}</span>
              <Icon name="chevronRight" size={18} strokeWidth={1.7} />
            </button>
          ) : (
            <button
              type="button"
              className={css.otherBtn}
              onClick={() => pickPeriod('current')}
              data-testid="review-current"
            >
              <span>{T('Tilbake til {p}', { p: p.periodShort ?? T('perioden') })}</span>
            </button>
          )}
          {tab === 'open' && lists.info.length > 0 && (
            <details className={css.info} data-testid="review-info">
              <summary>
                <Icon name="info" size={16} strokeWidth={1.7} />
                {T('Opplysninger uten handling ({n})', { n: lists.info.length })}
              </summary>
              <p>{T('Dette er opplysninger, ikke oppgaver. De øker ikke antallet du må følge opp.')}</p>
              <ul>
                {lists.info.map((c) => (
                  <li key={c.key}>
                    {[c.person?.name, c.what ?? c.title, c.dateLabel].filter(Boolean).join(', ')}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </>
    );
  };

  const listView = (l: List) => {
    const ask =
      p.onAskHugin && l.kind === 'area' && (l.area === 'planlegg' || l.area === 'timer' || l.area === 'lonn');
    return (
      <>
        <div className={css.queueHead}>
          <button
            type="button"
            className={css.crumb}
            onClick={() => openList(null)}
            data-testid="review-back"
          >
            <Icon name="chevronLeft" size={18} strokeWidth={1.7} /> {T('Alle arbeidsområder')}
          </button>
          <h2 ref={headRef} tabIndex={-1}>
            {listName} <span className={css.count2}>{shown.length}</span>
          </h2>
          {l.kind === 'scope' ? (
            !shown.length && (
              <p data-testid="review-scope">
                {T('Sakene for {label} er løst eller ikke lenger åpne.', { label: l.label })}
              </p>
            )
          ) : (
            <p>
              {T(AREAS.find((a) => a.a === l.area)?.sub ?? '')},{' '}
              {T(TABS.find(([k]) => k === tab)?.[1] ?? '').toLowerCase()}
            </p>
          )}
          {ask && (
            <button
              type="button"
              className={css.textLink}
              onClick={() =>
                p.onAskHugin?.(
                  T('Se på sakene i «{g}» i perioden og foreslå løsninger etter reglene.', { g: listName }),
                )
              }
            >
              <Icon name="sparkle" size={14} /> {T('La Hugin foreslå løsninger')}
            </button>
          )}
        </div>
        {notices}
        {shown.length ? (
          <ul className={css.rows}>{shown.map((c) => row(c, l.kind === 'scope', l))}</ul>
        ) : (
          l.kind === 'area' &&
          empty(T('Ingen oppgaver her'), T('Dette arbeidsområdet har ingen oppgaver i utvalget.'), {
            label: T('Til arbeidsområdene'),
            on: () => openList(null),
          })
        )}
      </>
    );
  };

  const nav = (
    <nav className={css.vnav} aria-label={T('Oppgavenavigasjon')}>
      <button type="button" className={css.crumb} onClick={back} disabled={busy} data-testid="review-back">
        <Icon name="chevronLeft" size={18} strokeWidth={1.7} /> {listName}
      </button>
      {active && at >= 0 && !finished && (
        <div className={css.pager}>
          <button
            type="button"
            aria-label={T('Forrige oppgave')}
            aria-keyshortcuts="K"
            disabled={busy || at <= 0}
            onClick={() => step(-1)}
          >
            <Icon name="chevronLeft" size={18} strokeWidth={1.7} />
          </button>
          <span className={css.position} data-testid="review-selector">
            {T('{i} av {n}', { i: at + 1, n: shown.length })}
          </span>
          <button
            type="button"
            aria-label={T('Neste oppgave')}
            aria-keyshortcuts="J"
            disabled={busy || at >= shown.length - 1}
            onClick={() => step(1)}
          >
            <Icon name="chevronRight" size={18} strokeWidth={1.7} />
          </button>
        </div>
      )}
    </nav>
  );

  const finishedView = (f: NonNullable<typeof finished>) => {
    const next = lists.open.find((c) => c.key !== f.c.key);
    return (
      <div className={css.done} data-testid="review-finished" data-key={f.c.key}>
        <span className={css.pill} data-state="ok">
          <Icon name="check" size={16} strokeWidth={1.7} />
          {T('Oppgaven er ferdig')}
        </span>
        <h2 ref={titleRef} tabIndex={-1}>
          {f.c.what ?? f.c.title}
        </h2>
        {(f.c.person || f.c.dateLabel) && (
          <p className={css.doneMeta}>
            {[f.c.person?.name, f.c.dateText ?? f.c.dateLabel].filter(Boolean).join(', ')}
          </p>
        )}
        <p data-testid="review-finished-text">{f.text}</p>
        {f.detail && <p className={css.doneMeta}>{f.detail}</p>}
        <p className={css.doneMeta}>{T('Du finner oppgaven under Ferdig.')}</p>
        <div className={css.doneActs}>
          <button
            type="button"
            className={css.primary}
            onClick={() => {
              if (next) return openCase(next);
              setFinished(null);
              openList(null);
            }}
            data-testid="review-next"
          >
            {next ? T('Til neste oppgave') : T('Til oversikten')}
          </button>
          <button
            type="button"
            className={css.quiet}
            onClick={() => {
              setFinished(null);
              pickTab('done');
            }}
          >
            {T('Se ferdige oppgaver')}
          </button>
          {f.undo && canWrite && (
            // a real inverse in the domain the page offers (approval taken back by the same route, a move moved back),
            // never a snapshot
            <button
              type="button"
              className={css.quiet}
              onClick={() => void undoFinished()}
              disabled={busy}
              data-testid="review-undo"
            >
              {T('Angre')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const detail = () => (
    <div className={css.detail} data-testid="review-case" data-key={(finished?.c ?? active)?.key}>
      {nav}
      {/* what the reader cannot do here is said on the task too, not only on the overview (S16, S17) */}
      {(p.readOnly || !online) && notices}
      {finished ? (
        finishedView(finished)
      ) : parked ? (
        <p className={css.notice} data-testid="review-parked" aria-live="polite">
          <Icon name="user" size={16} />
          <span>
            {T('Velger erstatter: {t}. Saken venter til du har valgt eller lukket panelet.', { t: parked })}
          </span>
        </p>
      ) : active ? (
        <CaseDetail
          c={active}
          busy={busy}
          error={error}
          titleRef={titleRef}
          outside={p.outside(active)}
          waiting={tab === 'waiting' || p.waiting.some((w) => w.key === active.key)}
          later={put.has(active.key)}
          laterUntil={deferrals[active.key]}
          canWrite={canWrite}
          readOnly={!!p.readOnly}
          impact={p.impact ? p.impact(active) : null}
          extra={p.extra ? p.extra(active) : null}
          about={p.about ? p.about(active) : null}
          onPerson={p.onPerson}
          onDay={p.onDay}
          onShowPeriod={() => p.onShowPeriod(active)}
          onAction={(a) => onAction(active, a)}
          onSubmit={(a, input) => void act(active, a, input)}
          onLater={() => onLater(active)}
          onDirty={onDirty}
        />
      ) : (
        <p className={css.none} aria-live="polite">
          {T('Saken er løst eller endret et annet sted.')}{' '}
          {shown[0] && (
            <button type="button" className={css.textLink} onClick={() => go((shown[0] as ReviewCase).key)}>
              {T('Gå til neste sak')}
            </button>
          )}
        </p>
      )}
    </div>
  );

  // the editor works on the task as last read: after a conflict the page reads again, the dialog shows what is there
  // now and the next save is checked against it, the typed draft kept (S18)
  const ec = editor ? (byKey.get(editor.c.key) ?? editor.c) : null;
  const editors = editor && ec && (
    <>
      {editor.kind === 'pause' && (
        <PauseDialog
          c={ec}
          open
          busy={busy}
          problem={problem}
          onClose={closeEditor}
          onSave={(v) =>
            void act(ec, editor.a as CaseAction, {
              lunchIn: v.lunchIn,
              lunchOut: v.lunchOut,
              reason: v.reason,
            })
          }
        />
      )}
      {editor.kind === 'plan' && (
        <PlanDialog
          c={ec}
          open
          busy={busy}
          problem={problem}
          onClose={closeEditor}
          onSave={(v) => void act(ec, editor.a as CaseAction, { start: v.start, end: v.end })}
        />
      )}
      {editor.kind === 'approve' && (
        <ApproveDialog
          c={ec}
          open
          busy={busy}
          problem={problem}
          onClose={closeEditor}
          onApprove={(ids) => void act(ec, editor.a as CaseAction, { ids })}
        />
      )}
      {editor.kind === 'decide' && (
        <DecisionDialog
          c={ec}
          ok={editor.a.ok}
          open
          busy={busy}
          problem={problem}
          onClose={closeEditor}
          onDecide={(reason) => void act(ec, editor.a, { reason })}
        />
      )}
      {editor.kind === 'defer' && (
        <DeferDialog
          c={ec}
          open
          today={today}
          onClose={closeEditor}
          onDefer={(d, r) => void defer(ec, d, r)}
        />
      )}
    </>
  );

  const levels = (
    <>
      {active || gone || finished ? detail() : list ? listView(list) : overview()}
      {receipt && (
        <div className={css.notice} data-tone="warn" data-testid="review-receipt" aria-live="polite">
          <Icon name="warn" size={18} strokeWidth={1.7} />
          <span>{`${receipt.text} ${receipt.message}`}</span>
          <button
            type="button"
            className={css.iconBtn}
            aria-label={T('Skjul')}
            onClick={() => setReceipt(null)}
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </>
  );

  const chrome = (
    <>
      {scopeBar}
      <div className={css.scroll} data-testid="saker-scroll">
        {levels}
      </div>
      {editors}
      <HelpDialog open={help} onClose={() => setHelp(false)} />
      {p.onScopeChange && p.weeks && (
        <ScopeDialog
          open={scopeOpen}
          weeks={p.weeks}
          monday={p.monday ?? p.weeks[0]?.monday ?? ''}
          teams={p.teams ?? []}
          teamId={p.teamId ?? null}
          onClose={() => setScopeOpen(false)}
          onApply={(v) => {
            setScopeOpen(false);
            setPeriod('current');
            setList(null);
            go(null);
            p.onScopeChange?.(v);
          }}
        />
      )}
      <output className="sr-only" aria-live="polite" aria-atomic="true" data-testid="saker-announcer">
        {announce}
      </output>
    </>
  );

  if (panel)
    return (
      <section ref={rootRef} className={css.saker} data-testid="review" aria-label={T('Saker')}>
        {chrome}
      </section>
    );

  // the phone: what is to do in the period and team, and the same levels in a sheet (S19)
  const total = lists.open.length;
  return (
    <section ref={rootRef} className={css.root} data-testid="review" aria-label={T('Saker')}>
      <div className={css.summary}>
        <div className={css.summaryText}>
          <strong data-testid="review-total">
            {unknown
              ? T('Oppgaveantallet er ukjent')
              : counting
                ? T('Henter oppgaver')
                : known === 'failed'
                  ? T('Minst {t} å følge opp', { t: tasks(total) })
                  : T('{t} å følge opp', { t: tasks(total) })}
          </strong>
          <span className={css.summaryScope}>
            {[p.periodLabel, p.team?.name ?? T('Alle team')].filter(Boolean).join(', ')}
          </span>
        </div>
        <button
          type="button"
          className={css.primary}
          onClick={() => {
            setOpen(true);
            if (!activeKey) setList(null);
          }}
          data-testid="review-open"
        >
          {T('Vis saker')}
        </button>
      </div>
      <Drawer
        open={open}
        onClose={() => {
          if (busyRef.current) return;
          // nothing half done is kept; closing resolves nothing (UC11)
          reset();
          setOpen(false);
          setActiveKey(null);
          setGone(null);
          setFinished(null);
        }}
        title={T('Saker')}
        className={css.drawer}
      >
        <div className={`${css.saker} ${css.sheet}`}>{chrome}</div>
      </Drawer>
    </section>
  );
}
